import { faSpotify } from "@fortawesome/free-brands-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Col, Form, Input, Row, Select, Spin } from "antd";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { MenuHeader } from "shared/components";
import { artistRelationshipList } from "shared/enums/ArtistRelationshipEnum";
import { CopyrightAssociationEnum } from "shared/enums/CopyrightAssociationEnum";
import {
  YoutubeFilled,
  InstagramOutlined,
  InfoCircleOutlined,
  LoadingOutlined,
} from "@ant-design/icons";
import SocialList from "./SocialList";
import {
  fetchArtistQuiz,
  fetchSpotifyArtists,
  fetchYoutubeArtists,
  saveQuiz,
  fetchUserIpLocation,
  fetchInstagramList,
} from "shared/service/StrmService";
import snakeToCamelCase from "shared/helpers/snakeToCamelCase";
import {
  DISTRO_PARAM,
  PRICING_PARAM,
  QUIZ_INFO,
} from "shared/constants/StorageConsts";
import {
  autenticado,
  handlePendingQuiz,
  hasQuizPending,
} from "shared/helpers/auth-functions";
import { getLoggedUser } from "explorer/reducers/users";
import "./Quiz.scss";
import SingleMultiselect, {
  filterOptionDiacritics,
} from "shared/components/single-multiselect/SingleMultiselect";
import { getLoggedUserArtist } from "explorer/reducers/artistas";
import QuizTheme from "shared/themes/QuizTheme";
import { isMobile } from "react-device-detect";
import { faChevronLeft } from "@fortawesome/free-solid-svg-icons";
import { validatePhone } from "shared/helpers/phone-helpers";
import { LinguagemEnum } from "shared/enums/LinguagemEnum";
import { toast } from "react-toastify";
import { saveSessionSpecialCampaign } from "shared/helpers/special-campaign-helpers";
import {
  tagManagerEvent,
  dataLayerViewedEvent,
  dataLayerClickedEvent,
} from "event-tracker/gtm-events";
import { DefaultOptionType } from "antd/es/select";
import { QuizButtonClickEventName } from "event-tracker/button-click-event-name";
import PhoneWithDDD from "shared/components/phone-with-ddd/PhoneWithDDD";
import { getMixPanelParams } from "shared/helpers/cookie-helpers";
import { MonitoredDataState } from "explorer/reducers/monitored-data";
import { RouteComponentProps } from "react-router-dom";
import logger from "shared/service/LoggerService";

// const hasURL = (value: string) => value.includes("http") || value.includes("/");

const getQuizValueIfPendingQuiz = (lang: string) => {
  const quizInfo = JSON.parse(sessionStorage.getItem(QUIZ_INFO) || "{}");

  if (hasQuizPending()) {
    return quizInfo;
  }

  const defaultValues: any = {
    [LinguagemEnum.Portugues]: {
      countryCode: "BR",
      countryCodePhone: "BR",
      ddi: "+55",
    },
    [LinguagemEnum.Ingles]: {
      countryCode: "US",
      countryCodePhone: "US",
      ddi: "+1",
    },
  };

  return (
    defaultValues[lang] || { countryCode: "", countryCodePhone: "", ddi: "" }
  );
};

enum Steps {
  PERSONAL_DATA = 0,
  PLATFORMS = 1,
  FINISH = 2,
}

const Quiz: React.FC<RouteComponentProps> = (props) => {
  const { history, location } = props;
  const urlSearch = useMemo(
    () => new URLSearchParams(window.location.search),
    []
  );
  const id = (location.state as any)?.id;
  const [form] = Form.useForm();
  const countryCodeValue = Form.useWatch("countryCode", form);
  const { t, i18n } = useTranslation();
  const [loading, setLoading] = useState<boolean>(false);
  const dispatch = useDispatch();
  const { genreOptions, genres } = useSelector((state: any) => state.genres);
  const { countryOptions } = useSelector((state: any) => state.countries);
  const { totalMonitoredArtists } = useSelector(
    (state: MonitoredDataState) => state.monitoredData
  );
  const [simpleForm] = useState<boolean>(urlSearch.get("simple") === "true");
  const [copyrightAssociationList, setCopyrightAssociationList] = useState<
    DefaultOptionType[]
  >([]);
  const [specialCampaign] = useState<boolean>(
    urlSearch.get("special-campaign") !== null
  );
  const [distroFlow] = useState(urlSearch.get("distro") === "true");
  const [pricingFlow] = useState(urlSearch.get("pricing") === "true");
  const [current, setCurrent] = useState<Steps>(Steps.PERSONAL_DATA);

  const [artistQuiz, setArtistQuiz] = useState<any>(
    getQuizValueIfPendingQuiz(i18n.language)
  );

  const isInternational = countryCodeValue !== "BR";
  const backgroundCurrent =
    current === 0 ? "zero" : current === 1 ? "um" : "tres";

  const handleBack = () => {
    if (current === Steps.PLATFORMS) setCurrent(Steps.PERSONAL_DATA);
    else history.push("/");
  };

  const getQuizInfo = useCallback(async () => {
    if (id) {
      const values = await fetchArtistQuiz(id);
      const selectedGenreCode = values.genreCodes && values.genreCodes[0];
      const selectedGenre = genres?.find(
        (genre: any) => genre.code === selectedGenreCode
      );
      let genreFormPayload: any = { genreCodes: [] };

      if (selectedGenre?.code) {
        genreFormPayload = { genreCodes: [selectedGenre.code] };
      }

      setArtistQuiz({ ...values, ...genreFormPayload });
    } else {
      const { response }: any = await fetchUserIpLocation();
      if (response?.country != null) {
        setArtistQuiz((prevState: any) => {
          return {
            ...prevState,
            countryCodePhone: response.countryCode,
            countryCode: response.countryCode,
            state: response.state,
            city: response.city,
          };
        });
      }
    }
  }, [id, genres]);

  const handleNext = async () => {
    if (current === Steps.PERSONAL_DATA) {
      dataLayerClickedEvent(QuizButtonClickEventName.NEXT, {
        ...tagManagerEvent.Quiz.S4A_QUIZ_GENERAL_DATA,
        specialCampaign: urlSearch.get("special-campaign"),
      });
      try {
        await form.validateFields([
          "name",
          "genreCodes",
          "countryCode",
          "phone",
          "ddi",
          "countryCodePhone",
        ]);
        setCurrent(Steps.PLATFORMS);
        window.scrollTo({ top: 0, behavior: "smooth" });
      } catch {}
    } else {
      dataLayerClickedEvent(QuizButtonClickEventName.NEXT, {
        ...tagManagerEvent.Quiz.S4A_QUIZ_PLATFORMS_CONNECT,
        specialCampaign: urlSearch.get("special-campaign"),
      });
      form.submit();
    }
  };

  const getSpotifyArtists = async (term: string, offset: number = 0) => {
    if (term) {
      dataLayerClickedEvent(QuizButtonClickEventName.SEARCH_SPOTIFY_PROFILE, {
        ...tagManagerEvent.Quiz.S4A_QUIZ_PLATFORMS_CONNECT,
        specialCampaign: urlSearch.get("special-campaign"),
      });
      const artists: any = await fetchSpotifyArtists(
        encodeURIComponent(term),
        countryCodeValue,
        offset
      );
      const artistsOption = artists.map(({ name, id, ...artist }: any) => ({
        name,
        id,
        avatarUrl: artist?.images[0]?.url,
      }));
      if (artistsOption.length === 0) {
        return [
          {
            name: t("quiz.pages.Questionario.spotifyNotFound"),
          },
        ];
      } else return artistsOption;
    }
  };

  const getYoutubeArtists = async (term: string) => {
    dataLayerClickedEvent(QuizButtonClickEventName.SEARCH_YOUTUBE_PROFILE, {
      ...tagManagerEvent.Quiz.S4A_QUIZ_PLATFORMS_CONNECT,
      specialCampaign: urlSearch.get("special-campaign"),
    });

    const artists: any = await fetchYoutubeArtists(encodeURIComponent(term));
    const artistsOption = artists.map((artist: any) => {
      return {
        id: artist.snippet?.channelId,
        name: artist.snippet?.title,
        avatarUrl: artist.snippet?.thumbnails?.medium?.url,
      };
    });
    if (artistsOption.length === 0) {
      return [
        {
          name: t("quiz.pages.Questionario.youtubeNotFound"),
        },
      ];
    } else return artistsOption;
  };

  const getInstagramArtists = async (term: string) => {
    // var regexp1 = new RegExp("^[0-9A-Za-z_.-]+$");
    // const termSemArroba = term.trim().replace(/@/g, "");
    // if (regexp1.test(termSemArroba)) {
    dataLayerClickedEvent(QuizButtonClickEventName.SEARCH_INSTAGRAM_PROFILE, {
      ...tagManagerEvent.Quiz.S4A_QUIZ_PLATFORMS_CONNECT,
      specialCampaign: urlSearch.get("special-campaign"),
    });

    try {
      const response = await fetchInstagramList(
        encodeURIComponent(term?.trim())
      );
      const profileList = snakeToCamelCase(response);
      const result = profileList.map((profile: any) => {
        return {
          id: profile.searchByUsername,
          name: profile.profile?.artistName,
          avatarUrl: profile.profile?.pictureUrl,
        };
      });
      if (profileList.length === 1) {
        const profile = profileList[0];
        if (!profile.profileFound || !profile.serviceAvailable) {
          return [
            {
              id: "no-data",
              name: t("quiz.pages.Questionario.instagramUserNotFound"),
            },
          ];
        }
      }
      if (result.length === 0) {
        return [
          {
            id: "no-data",
            name: t("quiz.pages.Questionario.instagramUserNotFound"),
          },
        ];
      }

      return result;
    } catch (error) {
      return [
        {
          name: t("quiz.pages.Questionario.instagramUserNotFound"),
          id: "no-data",
        },
      ];
    }
  };

  const processGenreOther: any = () => {
    const values = form.getFieldsValue(true);

    const genre = values.genreCodes && values.genreCodes[0];
    if (genre === "outro" && values?.genreAux) {
      const genreAux = values.genreAux;
      return {
        ...values,
        genreCodes: [genreAux],
      };
    } else return values;
  };

  const pendingQuizHandler = useCallback(async () => {
    if (autenticado() && hasQuizPending()) {
      const pricingFlow = sessionStorage.getItem(PRICING_PARAM) === "true";
      setLoading(true);
      setCurrent(Steps.FINISH);
      logger.message("User submitting quiz data", {
        transaction: "quiz_save",
        tags: {
          flow: "quiz_save",
          step: "form_submission",
          status: "authenticated",
        },
      });
      await handlePendingQuiz((success: boolean) => {
        if (success) {
          dispatch(getLoggedUser());
          dispatch(getLoggedUserArtist());

          logger.message("Quiz data saved successfully", {
            transaction: "quiz_save",
            tags: {
              flow: "quiz_save",
              step: "form_submission",
              status: "success",
            },
          });
          if (pricingFlow) {
            history.push("/pricing");
          } else {
            history.push("/estagio");
          }
        } else {
          toast.error<string>(t("quiz.pages.Questionario.errorSaveQuiz"));
          dispatch(getLoggedUser());
          dispatch(getLoggedUserArtist());
          logger.error("Error saving quiz data", {
            transaction: "quiz_save",
            tags: {
              flow: "quiz_save",
              step: "form_submission",
              status: "error",
            },
          });
          if (pricingFlow) {
            history.push("/pricing");
          } else {
            history.push("/estagio");
          }
        }
      });
    }
  }, [dispatch, history, t]);

  const onFinish = async () => {
    try {
      if (!loading) {
        setLoading(true);
        const values = processGenreOther();

        const request = { ...values };
        if (
          values.instagram.id === "no-data" &&
          values.spotify.id === "no-data" &&
          values.youtube.id === "no-data"
        ) {
          logger.warn(
            "User attempted to submit quiz without any platform data",
            {
              transaction: "quiz_creation",
              tags: {
                flow: "quiz_creation",
                step: "form_submission",
                error: "no_platforms",
              },
            }
          );
          toast.error<string>(t("quiz.pages.Questionario.atLeastOnePlatform"));
          setCurrent(Steps.PLATFORMS);
          setLoading(false);
          return;
        }

        if (autenticado()) {
          setCurrent(Steps.FINISH);
          try {
            logger.message("User submitting quiz data", {
              transaction: "quiz_creation",
              tags: {
                flow: "quiz_creation",
                step: "form_submission",
                status: "authenticated",
              },
            });

            await saveQuiz({
              ...request,
              utmTrackingData: getMixPanelParams(),
            });

            logger.message("Quiz data saved successfully", {
              transaction: "quiz_creation",
              tags: {
                flow: "quiz_creation",
                step: "form_submission",
                status: "success",
              },
            });

            dispatch(getLoggedUser());
            dispatch(getLoggedUserArtist());
            if ((location.state as any)?.diagnose) {
              history.push("/novo-diagnostico");
              return;
            }
            if (distroFlow) {
              history.push("/pricing");
              return;
            }
            history.push("/estagio");
          } catch (error) {
            logger.error(error, {
              transaction: "quiz_creation",
              tags: {
                flow: "quiz_creation",
                step: "form_submission",
              },
            });
            dispatch(getLoggedUser());
            dispatch(getLoggedUserArtist());
            if (pricingFlow) {
              history.push("/pricing");
            } else {
              history.push("/estagio");
            }
          }
        } else {
          logger.message(
            "Saving quiz data to session for unauthenticated user",
            {
              transaction: "quiz_creation",
              tags: {
                flow: "quiz_creation",
                step: "form_submission",
                status: "pending_authentication",
              },
            }
          );
          sessionStorage.setItem(
            QUIZ_INFO,
            JSON.stringify({
              ...request,
            })
          );
          if (distroFlow) sessionStorage.setItem(DISTRO_PARAM, "true");
          if (pricingFlow) sessionStorage.setItem(PRICING_PARAM, "true");
          history.push("/cadastre", { quiz: true });
        }
      }
    } catch (error) {
      logger.error(error, {
        transaction: "quiz_creation",
        tags: {
          flow: "quiz_creation",
          step: "form_submission",
        },
      });
      setCurrent(Steps.PLATFORMS);
      setLoading(false);
    }
  };

  useEffect(() => {
    form.resetFields();
  }, [form, artistQuiz]);

  useEffect(() => {
    getQuizInfo();
  }, [getQuizInfo]);

  useEffect(() => {
    setCopyrightAssociationList([
      {
        label: t("quiz.pages.Questionario.notAffiliated"),
        value: CopyrightAssociationEnum.NotAffiliated,
      },
      { label: "Abramus", value: CopyrightAssociationEnum.Abramus },
      { label: "Amar", value: CopyrightAssociationEnum.Amar },
      { label: "Assim", value: CopyrightAssociationEnum.Assim },
      { label: "Sbacem", value: CopyrightAssociationEnum.Sbacem },
      { label: "Sicam", value: CopyrightAssociationEnum.Sicam },
      { label: "Socinpro", value: CopyrightAssociationEnum.Socinpro },
      { label: "UBC", value: CopyrightAssociationEnum.UBC },
    ]);
  }, [t]);

  useEffect(() => {
    pendingQuizHandler();
  }, [pendingQuizHandler]);

  useEffect(() => {
    if (specialCampaign) {
      saveSessionSpecialCampaign(urlSearch.get("special-campaign"));
    }
  }, [specialCampaign, urlSearch]);

  useEffect(() => {
    if (current === Steps.PERSONAL_DATA) {
      dataLayerViewedEvent({
        ...tagManagerEvent.Quiz.S4A_QUIZ_GENERAL_DATA,
        specialCampaign: urlSearch.get("special-campaign"),
      });
    }
    if (current === Steps.PLATFORMS) {
      dataLayerViewedEvent({
        ...tagManagerEvent.Quiz.S4A_QUIZ_PLATFORMS_CONNECT,
        specialCampaign: urlSearch.get("special-campaign"),
      });
    }
    if (current === Steps.FINISH) {
      dataLayerViewedEvent({
        ...tagManagerEvent.Quiz.S4A_QUIZ_ANALYSIS_LOADING,
        specialCampaign: urlSearch.get("special-campaign"),
      });
    }
  }, [current, urlSearch]);

  if (loading)
    return (
      <div className={`quiz ${backgroundCurrent}`}>
        <MenuHeader current={current} />
        <div className="loading" style={{ marginTop: isMobile ? 30 : 120 }}>
          <div className="spacer"></div>
          <Spin
            indicator={
              <LoadingOutlined spin={false} style={{ color: "#F2F2F2" }} />
            }
            size="large"
          />
          <span className="aguarde">
            {t("quiz.pages.Questionario.loadingTitulo")}
          </span>
          <p className="texto" style={{ margin: isMobile ? "0 44px" : 0 }}>
            <Trans
              i18nKey={t("quiz.pages.Questionario.loadingTexto", {
                totalArtists: totalMonitoredArtists,
              })}
            />
          </p>
        </div>
      </div>
    );

  return (
    <div className={`quiz ${backgroundCurrent}`}>
      <QuizTheme>
        <MenuHeader current={current} />
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            position: "relative",
          }}
        >
          <div className="content">
            <Form
              layout="vertical"
              form={form}
              onFinish={onFinish}
              initialValues={artistQuiz}
            >
              <Row hidden={current !== Steps.PERSONAL_DATA} gutter={[18, 18]}>
                <>
                  {autenticado() && (
                    <span
                      onClick={handleBack}
                      className="voltar"
                      style={{
                        position: isMobile ? "relative" : "absolute",
                        left: isMobile ? 0 : 30,
                        top: 20,
                      }}
                    >
                      <FontAwesomeIcon icon={faChevronLeft} />
                      {t("quiz.pages.Questionario.back")}
                    </span>
                  )}
                  <h3 className="texto-passo">
                    {t("quiz.pages.Questionario.passoUm")}
                  </h3>
                  <div className="caixa-input">
                    <Col xs={24}>
                      <Form.Item
                        label={t("quiz.pages.Questionario.nameLabel")}
                        name="name"
                        rules={[
                          {
                            required: true,
                            message: t("quiz.pages.Questionario.nameRequired"),
                          },
                        ]}
                      >
                        <Input
                          placeholder={t(
                            "quiz.pages.Questionario.typeHerePlaceholder"
                          )}
                        />
                      </Form.Item>
                    </Col>
                    <Col xs={24}>
                      <Form.Item
                        label={t("quiz.pages.Questionario.genreLabel")}
                        name="genreCodes"
                        colon={false}
                        rules={[
                          {
                            required: true,
                            message: t("quiz.pages.Questionario.genreRequired"),
                          },
                        ]}
                      >
                        <SingleMultiselect
                          options={genreOptions}
                          optionFilterProp="label"
                          placeholder={t(
                            "quiz.pages.Questionario.selectHerePlaceholder"
                          )}
                        />
                      </Form.Item>
                    </Col>
                    {/* <Form.Item
                      noStyle
                      shouldUpdate={(prevValues, curValues) =>
                        prevValues.genreCodes !== curValues.genreCodes
                      }
                    >
                      {() => {
                        const genre = form.getFieldValue("genreCodes");
                        return (
                          <Col xs={24} hidden={!genre || genre[0] !== "outro"}>
                            <Form.Item
                              label={t("quiz.pages.Questionario.genreAuxLabel")}
                              name="genreAux"
                            >
                              <Select
                                options={revelatorGenres}
                                filterOption={filterOptionDiacritics}
                                showSearch
                                allowClear
                                placeholder={t(
                                  "quiz.pages.Questionario.selectHerePlaceholder"
                                )}
                              />
                            </Form.Item>
                          </Col>
                        );
                      }}
                    </Form.Item> */}
                    <Col xs={24} hidden={simpleForm}>
                      <Form.Item
                        label={t("quiz.pages.Questionario.relationLabel")}
                        colon={false}
                        name="artistRelationship"
                      >
                        <Select
                          options={artistRelationshipList.map(
                            ({ label, value }: any) => ({
                              label: t(`quiz.pages.Questionario.${label}`),
                              value,
                            })
                          )}
                          placeholder={t(
                            "quiz.pages.Questionario.selectHerePlaceholder"
                          )}
                        />
                      </Form.Item>
                    </Col>
                    <Col xs={24} hidden={simpleForm || isInternational}>
                      <Form.Item
                        label={t(
                          "quiz.pages.Questionario.copyrightAssociationLabel"
                        )}
                        colon={false}
                        name="copyrightAssociation"
                      >
                        <Select
                          options={copyrightAssociationList}
                          placeholder={t(
                            "quiz.pages.Questionario.selectHerePlaceholder"
                          )}
                        />
                      </Form.Item>
                    </Col>
                  </div>
                  <div className="caixa-input">
                    <Col xs={12}>
                      <Form.Item
                        label={t("quiz.pages.Questionario.countryLabel")}
                        colon={false}
                        name="countryCode"
                        rules={[
                          {
                            required: true,
                            message: t(
                              "quiz.pages.Questionario.countryRequired"
                            ),
                          },
                        ]}
                      >
                        <Select
                          showSearch
                          options={countryOptions}
                          filterOption={filterOptionDiacritics}
                          notFoundContent={t("quiz.pages.Questionario.noItem")}
                          placeholder={t(
                            "quiz.pages.Questionario.selectHerePlaceholder"
                          )}
                        />
                      </Form.Item>
                    </Col>
                    <Col xs={12} hidden={simpleForm}>
                      <Form.Item
                        label={t("quiz.pages.Questionario.stateLabel")}
                        name="state"
                        colon={false}
                      >
                        <Input
                          placeholder={t(
                            "quiz.pages.Questionario.typeHerePlaceholder"
                          )}
                        />
                      </Form.Item>
                    </Col>
                    <Col xs={24} hidden={simpleForm}>
                      <Form.Item
                        label={t("quiz.pages.Questionario.cityLabel")}
                        name="city"
                        colon={false}
                      >
                        <Input
                          placeholder={t(
                            "quiz.pages.Questionario.typeHerePlaceholder"
                          )}
                        />
                      </Form.Item>
                    </Col>
                    <Col xs={24} hidden={simpleForm}>
                      <Form.Item
                        label={t("quiz.pages.Questionario.phoneLabel")}
                        required={!simpleForm}
                      >
                        <PhoneWithDDD
                          names={{
                            ddi: "ddi",
                            phoneCountry: "countryCodePhone",
                            phone: "phone",
                          }}
                          rules={{
                            ddi: [
                              {
                                required: !simpleForm,
                                message: t(
                                  "quiz.pages.Questionario.dddRequired"
                                ),
                              },
                            ],
                            phone: [
                              {
                                required: !simpleForm,
                                message: t(
                                  "quiz.pages.Questionario.phoneRequired"
                                ),
                              },
                              {
                                validator(_: any, value: any) {
                                  const { countryCodePhone } =
                                    form.getFieldsValue();
                                  return validatePhone(
                                    countryCodePhone,
                                    value,
                                    t("pages.MeusDados.telefoneInvalido")
                                  );
                                },
                              },
                            ],
                          }}
                        />
                      </Form.Item>
                    </Col>
                  </div>
                </>
              </Row>
              <Row hidden={current !== Steps.PLATFORMS} gutter={[18, 18]}>
                <>
                  <span
                    onClick={handleBack}
                    className="voltar"
                    style={{
                      position: isMobile ? "relative" : "absolute",
                      left: isMobile ? 0 : 30,
                      top: 20,
                    }}
                  >
                    <FontAwesomeIcon icon={faChevronLeft} />
                    {t("quiz.pages.Questionario.back")}
                  </span>
                  <h3 className="texto-passo">
                    {t("quiz.pages.Questionario.passoDois")}
                  </h3>
                  <ol className="lista-instrucoes">
                    <li>
                      <Trans
                        i18nKey={t("quiz.pages.Questionario.intrucoesUm")}
                      />
                    </li>
                    <li>
                      <Trans
                        i18nKey={t("quiz.pages.Questionario.intrucoesDois")}
                      />
                    </li>
                    <li>
                      <Trans
                        i18nKey={t("quiz.pages.Questionario.intrucoesTres")}
                      />
                    </li>
                  </ol>
                  <Col xs={24}>
                    <Form.Item
                      name="spotify"
                      rules={[
                        {
                          required: true,
                          message: t("quiz.pages.Questionario.spotifyRequired"),
                        },
                      ]}
                    >
                      <SocialList
                        label={t("quiz.pages.Questionario.spotifyLabel")}
                        logo={
                          <FontAwesomeIcon
                            icon={faSpotify}
                            className="label-icon"
                          />
                        }
                        placeholder={t(
                          "quiz.pages.Questionario.searchPlaceholder"
                        )}
                        checkboxLabel={t("quiz.pages.Questionario.noSpotify")}
                        getListOptions={getSpotifyArtists}
                        showMore
                      />
                    </Form.Item>
                  </Col>
                  <Col xs={24}>
                    <Form.Item
                      name="youtube"
                      rules={[
                        {
                          required: true,
                          message: t("quiz.pages.Questionario.youtubeRequired"),
                        },
                      ]}
                    >
                      <SocialList
                        label={t("quiz.pages.Questionario.youtubeLabel")}
                        logo={<YoutubeFilled className="label-icon" />}
                        placeholder={t(
                          "quiz.pages.Questionario.searchPlaceholder"
                        )}
                        checkboxLabel={t("quiz.pages.Questionario.noYoutube")}
                        getListOptions={getYoutubeArtists}
                      />
                    </Form.Item>
                  </Col>
                  <Col xs={24}>
                    <Form.Item
                      name="instagram"
                      rules={[
                        {
                          required: true,
                          message: t(
                            "quiz.pages.Questionario.instagramRequired"
                          ),
                        },
                      ]}
                    >
                      <SocialList
                        label={t("quiz.pages.Questionario.instagramLabel")}
                        logo={<InstagramOutlined className="label-icon" />}
                        placeholder={t(
                          "quiz.pages.Questionario.searchPlaceholder"
                        )}
                        checkboxLabel={t("quiz.pages.Questionario.noInstagram")}
                        getListOptions={getInstagramArtists}
                        prefix={"@"}
                      />
                    </Form.Item>
                  </Col>
                  <Col xs={24}>
                    <div className="aviso-social">
                      <InfoCircleOutlined style={{ marginTop: 6 }} />
                      <p>{t("quiz.pages.Questionario.avisoLinkSocial")}</p>
                    </div>
                  </Col>
                </>
              </Row>
            </Form>
            <Row>
              <Col
                xs={24}
                style={{
                  display: "flex",
                  justifyContent: "center",
                  margin: "32px 0",
                }}
              >
                {/* {autenticado() || current === 1 ? (
                  <button
                    className="botao-borda-roxa"
                    onClick={handleBack}
                    disabled={loading}
                  >
                    {t("quiz.pages.Questionario.back")}
                  </button>
                ) : (
                  <span></span>
                )} */}
                <button
                  className="botao-roxo continuar"
                  onClick={handleNext}
                  type="button"
                  disabled={loading}
                >
                  {t("quiz.pages.Questionario.confirm")}
                </button>
              </Col>
            </Row>
          </div>
        </div>
      </QuizTheme>
    </div>
  );
};

export default Quiz;
